import React, {useState} from "react";
import {Button, Card, CardBody, CardHeader, CardTitle, FormFeedback, Input} from "reactstrap";
import {IExoTemplateForm} from "../../../../store/exo/types";
import TelegramEditor from "../TelegramEditor/TelegramEditor";
import FeatherIcon from "feather-icons-react";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {ExoService} from "../../../../services/exo.service";
import * as yup from "yup";
import {useFormik} from "formik";
import {telegramTextLength} from "../TelegramEditor/telegramHtml";

interface FormCardExoTemplateProps {
    templateId?: number | null;
    onClose: () => void;
}

const FormCardExoTemplate: React.FC<FormCardExoTemplateProps> = ({
                                                                     templateId,
                                                                     onClose,
                                                                 }) => {
    const isEditTemplate = !!templateId;
    const queryClient = useQueryClient();

    const [approvedSave, setApprovedSave] = useState<boolean>(false)

    const {data: exoTemplate, isFetching} = useQuery({
        queryKey: ["keyExoTemplate", templateId],
        queryFn: () => ExoService.getByIdTemplate(templateId || -1),
        enabled: isEditTemplate
    });

    const onSuccess = () => {
        queryClient.invalidateQueries({queryKey: ["keyExoTemplates"]})
            .then(() => onClose())
    }

    const onError = () => {
        setApprovedSave(false);
    }

    const {mutate: createTemplate, isPending: isPendingCreate} = useMutation({
        mutationFn: (data: IExoTemplateForm) => ExoService.createTemplate(data),
        onSuccess: onSuccess,
        onError: onError,
        mutationKey: ["keyCreateExoTemplate"],
    });

    const {mutate: updateTemplate, isPending: isPendingUpdate} = useMutation({
        mutationFn: (data: IExoTemplateForm) => ExoService.updateTemplate(templateId!, data),
        onSuccess: onSuccess,
        onError: onError,
        mutationKey: ["keyUpdateExoTemplate"],
    });

    const handleSubmitForm = (values: IExoTemplateForm) => {
        setApprovedSave(true)

        if (!isEditTemplate) createTemplate(values)
        else updateTemplate(values)
    }

    const validationSchema: yup.ObjectSchema<IExoTemplateForm> = yup.object({
        name: yup.string()
            .trim()
            .required("Name is required")
            .max(64, "Name must be at most 64 characters"),
        body_msg: yup.string()
            .trim()
            .required("Message is required")
            .test("tg-length", "Message must be at most 4096 characters",
                v => telegramTextLength(v ?? "") <= 4096),
    })

    const initialValues: IExoTemplateForm = {
        name: exoTemplate?.name || "",
        body_msg: exoTemplate?.body_msg || ""
    };

    const formik = useFormik<IExoTemplateForm>({
        initialValues: initialValues,
        onSubmit: handleSubmitForm,
        validationSchema: validationSchema,
        validateOnBlur: true,
        validateOnChange: true,
        validateOnMount: true,
        enableReinitialize: true
    });

    const handleCancelClick = () => {
        formik.resetForm();
        onClose();
    }

    const fieldInvalid = (name: keyof IExoTemplateForm) => !!(formik.touched[name] && formik.errors[name]);

    const isPendingSave = (isPendingCreate || isPendingUpdate || !!queryClient.isFetching({queryKey: ["keyExoTemplates"]})) && approvedSave;

    return (
        <Card>
            <form onSubmit={formik.handleSubmit}>
                <CardHeader className="p-3">
                    <div className="d-flex align-items-center justify-content-between">
                        <CardTitle tag="h5" className="w-75 m-0">
                            <Input
                                id="name"
                                name="name"
                                placeholder={isFetching ? "Loading..." : "Enter Name..."}
                                value={formik.values.name}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                invalid={fieldInvalid("name")}
                                disabled={isFetching || isPendingSave}
                                className="fs-5"
                                autoFocus
                            />
                        </CardTitle>

                        <div className="d-flex align-items-center gap-1">
                            <Button className="d-flex p-1" color="danger" onClick={handleCancelClick}
                                    disabled={isPendingSave}>
                                <FeatherIcon icon="x" size={16}/>
                            </Button>

                            <Button className="d-flex p-1" color="success" type="submit"
                                    disabled={isPendingSave}>
                                {isPendingSave ?
                                    <i className="mdi mdi-loading mdi-spin font-size-16 lh-1"></i> :
                                    <FeatherIcon icon="check" size={16}/>}
                            </Button>
                        </div>
                    </div>
                </CardHeader>
                <CardBody className="p-3">
                    <TelegramEditor
                        value={formik.values.body_msg}
                        onChange={(value) => formik.setFieldValue("body_msg", value)}
                        onBlur={() => formik.setFieldTouched("body_msg", true)}
                        disabled={isFetching || isPendingSave}
                        invalid={fieldInvalid("body_msg")}
                    />
                    <FormFeedback>{formik.errors.body_msg}</FormFeedback>
                </CardBody>
            </form>
        </Card>
    )
};

export default FormCardExoTemplate;